/**
 * Central SEO configuration + helpers.
 *
 * SITE_URL must be the public origin (no trailing slash) so that canonical /
 * og:url values are absolute - relative URLs are ignored by Google News,
 * Facebook and Twitter.
 */
export const SITE = {
    url: (process.env.REACT_APP_SITE_URL || "https://etawahtimes.in").replace(/\/+$/, ""),
    name: "Etawah Times",
    locale: "hi_IN",
    language: "hi-IN",
    defaultTitle: "Etawah Times - Latest News from Etawah & Uttar Pradesh",
    defaultDescription:
        "Get the latest breaking news from Etawah, Uttar Pradesh. राजनीति, अपराध, शिक्षा, स्थानीय समाचार और अपडेट्स - Etawah Times पर पढ़ें।",
    defaultKeywords:
        "Etawah News, Uttar Pradesh News, Etawah Hindi News, Local News Etawah, UP News Today, Etawah Samachar",
    defaultSection: "Local News",
    defaultTags: ["Etawah", "UP News"],
    // max-image-preview:large is what lets Google News / Discover use a full
    // size thumbnail; without it they fall back to a thumbnail-sized crop.
    defaultRobots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    // Publisher logo for schema.org. NOT logo512.png - that is still CRA's
    // default React logo, which would show up as the Etawah Times brand mark.
    logo: "/logo.png",
    // 1200x630 branded card used as the og:image / twitter:image fallback.
    ogImage: "/og-image.png",
};

/** Build an absolute URL from a path or an already absolute URL. */
export const absoluteUrl = (pathOrUrl = "/") => {
    if (!pathOrUrl) return SITE.url;
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    return `${SITE.url}/${String(pathOrUrl).replace(/^\/+/, "")}`;
};

/**
 * Convert a MySQL date / datetime ("2026-07-24" or "2026-07-24 10:00:00")
 * into the ISO-8601 form Google News expects: 2026-07-24T10:00:00+05:30
 *
 * The backend stores timestamps in IST, so the offset is fixed at +05:30.
 * Values that are already ISO-8601 with an offset are returned untouched.
 */
export const toIstIso = (value) => {
    if (!value) return undefined;
    const raw = String(value).trim();

    if (/[+-]\d{2}:\d{2}$/.test(raw) || /Z$/.test(raw)) return raw;

    const parts = raw.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (!parts) return undefined;

    const [, year, month, day, hour = "00", minute = "00", second = "00"] = parts;
    return `${year}-${month}-${day}T${hour}:${minute}:${second}+05:30`;
};

/** Strip HTML and clamp to a meta-description friendly length. */
export const toMetaDescription = (html, fallback = SITE.defaultDescription, max = 160) => {
    if (!html) return fallback;
    const text = String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!text) return fallback;
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
};

/** "Etawah, UP News" | ["Etawah", "UP News"] -> ["Etawah", "UP News"] */
export const toTagList = (tags) => {
    if (!tags) return SITE.defaultTags;
    const list = Array.isArray(tags) ? tags : String(tags).split(",");
    const cleaned = list.map((tag) => String(tag).trim()).filter(Boolean);
    return cleaned.length ? cleaned : SITE.defaultTags;
};
